import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  HomeIcon,
  CubeIcon, 
  ArchiveBoxIcon, 
  TruckIcon,
  UsersIcon, 
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ShoppingBagIcon,
  BuildingOfficeIcon,
  ArrowUturnLeftIcon,
  DocumentTextIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  BellIcon,
  MoonIcon,
  SunIcon,
  XMarkIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = () => {
  // Change default to false so sidebar is closed initially
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { currentUser, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { language, t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detect screen size for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Don't automatically open sidebar based on screen size
      // Let user control it manually
    };
    
    // Set initial state
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    console.log("Toggling sidebar from", sidebarOpen, "to", !sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  const toggleMobileSidebar = () => {
    setSidebarMobileOpen(!sidebarMobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Échec de déconnexion", error);
    }
  };

  const menuItems = [
    { id: 'dashboard', label: t('dashboard').toUpperCase(), icon: HomeIcon, path: '/dashboard' },
    { id: 'products', label: t('products').toUpperCase(), icon: CubeIcon, path: '/products' },
    { id: 'stock', label: t('stock').toUpperCase(), icon: ArchiveBoxIcon, path: '/stock' },
    { id: 'suppliers', label: t('suppliers').toUpperCase(), icon: TruckIcon, path: '/suppliers' },
    { id: 'customers', label: t('customers').toUpperCase(), icon: UsersIcon, path: '/customers' },
    { id: 'orders', label: t('orders').toUpperCase(), icon: ClipboardDocumentListIcon, path: '/orders' },
    { id: 'sales', label: t('sales').toUpperCase(), icon: BanknotesIcon, path: '/sales' },
    { id: 'purchases', label: t('purchases').toUpperCase(), icon: ShoppingBagIcon, path: '/purchases' },
    { id: 'warehouses', label: t('warehouses').toUpperCase(), icon: BuildingOfficeIcon, path: '/warehouses' },
    { id: 'returns', label: t('returns').toUpperCase(), icon: ArrowUturnLeftIcon, path: '/returns' },
    { id: 'documents', label: t('documents').toUpperCase(), icon: DocumentTextIcon, path: '/documents' },
    
    { id: 'reports', label: t('reports').toUpperCase(), icon: ChartBarIcon, path: '/reports' },
    { id: 'advanced-analytics', label: t('advancedAnalytics').toUpperCase(), icon: PresentationChartLineIcon, path: '/advanced-analytics' },
    { id: 'employees', label: t('employees').toUpperCase(), icon: UserGroupIcon, path: '/employees' },
    { id: 'settings', label: t('settings').toUpperCase(), icon: Cog6ToothIcon, path: '/settings' },
  ];

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const closeSidebar = () => {
    if (window.innerWidth < 1024) {
      setSidebarMobileOpen(false);
    }
  };

  const activeMenuItem = menuItems.find(item => isActive(item.path));

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <AnimatePresence mode="wait">
          {sidebarOpen && (
          <motion.div
              key="desktop-sidebar"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`${isRTL ? 'order-2' : 'order-1'} relative z-30 hidden lg:block`}
          >
            <aside className="flex flex-col w-64 bg-white/5 backdrop-blur-xl border-r border-white/10 fixed inset-y-0 z-10">
              <div className="h-full flex flex-col">
                <div className="flex-shrink-0 pt-5 pb-4 overflow-hidden">
                    <div className="flex items-center flex-shrink-0 px-4 justify-between">
                      <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-red-600 rounded-xl flex items-center justify-center mr-3">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375Z" />
                        <path fillRule="evenodd" d="m3.087 9 .54 9.176A3 3 0 0 0 6.62 21h10.757a3 3 0 0 0 2.995-2.824L20.913 9H3.087Zm6.133 2.845a.75.75 0 0 1 1.06 0l1.72 1.72 1.72-1.72a.75.75 0 1 1 1.06 1.06l-1.72 1.72 1.72 1.72a.75.75 0 1 1-1.06 1.06L12 15.685l-1.72 1.72a.75.75 0 1 1-1.06-1.06l1.72-1.72-1.72-1.72a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-lg font-semibold text-white">STOCKPRO</h1>
                      <p className="text-xs text-white/60">{language === 'fr' ? 'Gestion d\'inventaire' : 'إدارة المخزون'}</p>
                    </div>
                      </div>
                      
                      {/* Add a prominent close button */}
                      <button
                        onClick={toggleSidebar}
                        className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-lg text-white"
                        aria-label="Close sidebar"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                  </div>
                </div>

                <nav className="mt-8 flex-1 px-2 overflow-y-auto scrollbar-hide">
                  <div className="space-y-1">
                    {menuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          navigate(item.path);
                          closeSidebar();
                        }}
                        className={`group flex items-center w-full px-2 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive(item.path)
                            ? 'bg-white/10 text-white'
                            : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <item.icon
                          className="flex-shrink-0 h-5 w-5 mr-3"
                          aria-hidden="true"
                        />
                        <span className="text-xs font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            </aside>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col overflow-hidden ${isRTL ? 'order-1' : 'order-2'} transition-all duration-300`}>
          {/* Header */}
          <header className="h-16 bg-white/10 backdrop-blur-lg border-b border-white/20 flex items-center justify-between px-3 sm:px-4 md:px-6 z-20">
            {/* Left side - Menu button and page title */}
            <div className="flex items-center">
              <button
                className="lg:hidden text-white/80 hover:text-white mr-3"
                onClick={toggleMobileSidebar}
                aria-label="Menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              {/* Prominent sidebar toggle button */}
              <button 
                onClick={toggleSidebar} 
                className="text-white hover:text-white bg-red-500/20 hover:bg-red-500/30 transition-all duration-200 p-2 rounded-lg mr-3 flex items-center justify-center"
                aria-label="Toggle sidebar"
              >
                {sidebarOpen ? (
                  <XMarkIcon className="h-5 w-5" />
                ) : (
                  <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                    <span className="ml-1 text-xs font-bold sm:inline">Menu</span>
                  </div>
                )}
              </button>
              
              <h1 className={`font-semibold text-white ${isMobile ? 'text-base truncate max-w-[180px]' : 'text-xl'}`}>
                {activeMenuItem?.label || t('dashboard').toUpperCase()}
              </h1>
            </div>

            {/* Right side - User menu, theme toggle, etc. */}
            <div className="flex items-center">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-1.5 sm:p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 mr-1 sm:mr-2"
                aria-label="Toggle theme"
              >
                {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>

              {/* Notifications - Hidden on smallest screens */}
              <button 
                className="hidden sm:block p-1.5 sm:p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 mr-1 sm:mr-2"
                aria-label="Notifications"
              >
                <BellIcon className="h-5 w-5" />
              </button>

              {/* User menu */}
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-red-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                  {currentUser?.name?.charAt(0) || "U"}
                </div>
              </div>
            </div>
          </header>

          {/* Main Content Area */}
          <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-7xl mx-auto"
            >
              <Outlet />
            </motion.div>
          </main>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMobileSidebar}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-20 lg:hidden"
            />
            <motion.div
              initial={{ x: isRTL ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? '100%' : '-100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={`fixed inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-30 w-64 lg:hidden`}
            >
              <aside className="flex flex-col w-64 h-full bg-white/5 backdrop-blur-xl border-r border-white/10 shadow-xl">
                <div className="flex items-center justify-between px-4 py-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-red-600 rounded-xl flex items-center justify-center mr-3">
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375Z" />
                        <path fillRule="evenodd" d="m3.087 9 .54 9.176A3 3 0 0 0 6.62 21h10.757a3 3 0 0 0 2.995-2.824L20.913 9H3.087Zm6.133 2.845a.75.75 0 0 1 1.06 0l1.72 1.72 1.72-1.72a.75.75 0 1 1 1.06 1.06l-1.72 1.72 1.72 1.72a.75.75 0 1 1-1.06 1.06L12 15.685l-1.72 1.72a.75.75 0 1 1-1.06-1.06l1.72-1.72-1.72-1.72a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-lg font-semibold text-white">STOCKPRO</h1>
                      <p className="text-xs text-white/60">{language === 'fr' ? 'Gestion d\'inventaire' : 'إدارة المخزون'}</p>
                    </div>
                  </div>
                  <button
                    onClick={toggleMobileSidebar}
                    className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors"
                    aria-label="Close menu"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center justify-between px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-red-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                      {currentUser?.name?.charAt(0) || "U"}
                    </div>
                    <div className="text-sm text-white/80">
                      {currentUser?.name || (language === 'fr' ? "Utilisateur" : "مستخدم")}
                    </div>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10"
                    aria-label="Toggle theme"
                  >
                    {isDark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
                  </button>
                </div>

                <div className="mt-2 px-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={language === 'fr' ? 'Rechercher...' : 'بحث...'}
                      className="w-full bg-white/10 border border-white/20 rounded-lg pl-8 pr-4 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-2.5 top-2.5 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                <nav className="mt-4 flex-1 px-2 overflow-y-auto scrollbar-hide">
                  <div className="space-y-1">
                    {menuItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          navigate(item.path);
                          closeSidebar();
                        }}
                        className={`group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive(item.path)
                            ? 'bg-gradient-to-r from-blue-600/80 to-indigo-600/80 text-white shadow-md'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <item.icon
                          className="flex-shrink-0 h-5 w-5 mr-3"
                          aria-hidden="true"
                        />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </nav>
                
                <div className="p-4">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {t('logout')}
                  </button>
                </div>
              </aside>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout; 